import { Box, Collapse, useDisclosure } from '@chakra-ui/react';
import { Button, Callout } from '@main/complykit';
import type { Namespace, TFunction } from 'i18next';
import { useCallback, useRef, useState } from 'react';
import z from 'zod/v4';

import {
  ConnectorConfigForm,
  type ConnectorConfigSubmit,
  type ConnectorConfigSubmitResult,
  type EncryptedConfigFieldState,
} from '../connector-config-form';

export type ConnectorConfigState = {
  config: Record<string, unknown>;
  encryptedFields: EncryptedConfigFieldState[];
};

export type ConnectorConfigPersistence = {
  load: () => Promise<ConnectorConfigState>;
  save: (value: ConnectorConfigSubmit) => Promise<ConnectorConfigState>;
};

export type ConnectedConnectorConfigEditor = {
  isOpen: boolean;
  isLoading: boolean;
  hasLoadError: boolean;
  configState: ConnectorConfigState | null;
  revision: number;
  open: () => Promise<void>;
  close: () => void;
  toggle: () => Promise<void>;
  retry: () => Promise<void>;
  submit: (value: ConnectorConfigSubmit) => Promise<ConnectorConfigSubmitResult>;
};

export function useConnectedConnectorConfigEditor(
  configPersistence: ConnectorConfigPersistence,
): ConnectedConnectorConfigEditor {
  const collapse = useDisclosure();
  const requestIdRef = useRef(0);
  const [configState, setConfigState] = useState<ConnectorConfigState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [revision, setRevision] = useState(0);

  const replaceConfigState = useCallback((nextConfigState: ConnectorConfigState) => {
    setConfigState(nextConfigState);
    setHasLoadError(false);
    setRevision((current) => current + 1);
  }, []);

  const load = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsLoading(true);
    setHasLoadError(false);
    setConfigState(null);

    try {
      const nextConfigState = await configPersistence.load();
      if (requestIdRef.current === requestId) {
        replaceConfigState(nextConfigState);
      }
    } catch {
      if (requestIdRef.current === requestId) {
        setHasLoadError(true);
      }
    } finally {
      if (requestIdRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [configPersistence, replaceConfigState]);

  const open = useCallback(async () => {
    await load();
    collapse.onOpen();
  }, [collapse, load]);

  const close = useCallback(() => {
    collapse.onClose();
  }, [collapse]);

  const toggle = useCallback(async () => {
    if (collapse.isOpen) {
      close();
      return;
    }

    await open();
  }, [close, collapse.isOpen, open]);

  const submit = useCallback(
    async (value: ConnectorConfigSubmit): Promise<ConnectorConfigSubmitResult> => {
      const nextConfigState = await configPersistence.save(value);
      replaceConfigState(nextConfigState);
      return { status: 'committed' };
    },
    [configPersistence, replaceConfigState],
  );

  return {
    isOpen: collapse.isOpen,
    isLoading,
    hasLoadError,
    configState,
    revision,
    open,
    close,
    toggle,
    retry: load,
    submit,
  };
}

export type ConnectorConfigCollapseProps = {
  editor: ConnectedConnectorConfigEditor;
  t: TFunction<Namespace>;
  tKey: string;
  configSchema: z.ZodObject;
  encryptedConfigSchema: z.ZodObject;
  submitLabel: string;
};

export const ConnectorConfigCollapse = ({
  editor,
  t,
  tKey,
  configSchema,
  encryptedConfigSchema,
  submitLabel,
}: ConnectorConfigCollapseProps) => {
  return (
    <Collapse in={editor.isOpen} unmountOnExit>
      <Box mt={8}>
        {editor.hasLoadError && (
          <Callout
            status="error"
            action={
              <Button
                tone="critical"
                variant="outline"
                isPending={editor.isLoading}
                onPress={() => void editor.retry()}
              >
                {t('configureForm.retryLoadConfigBtn', { defaultValue: 'Retry' })}
              </Button>
            }
          >
            {t('configureForm.loadConfigError', {
              defaultValue: 'Could not load connector configuration.',
            })}
          </Callout>
        )}
        {editor.configState && (
          <ConnectorConfigForm
            key={editor.revision}
            t={t}
            tKey={tKey}
            configSchema={configSchema}
            encryptedConfigSchema={encryptedConfigSchema}
            defaultConfig={editor.configState.config}
            encryptedFields={editor.configState.encryptedFields}
            submitLabel={submitLabel}
            requireDirtyToSubmit={true}
            onCancel={editor.close}
            onSubmit={editor.submit}
          />
        )}
      </Box>
    </Collapse>
  );
};
